import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import type { User } from '../stores/authStore';

interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isAdmin: boolean;
  isManager: boolean;
  isOperator: boolean;
  isViewer: boolean;
}

export function useAuth(): UseAuthReturn {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    login: storeLogin,
    loginWithSupabase,
    logout: storeLogout,
    clearError,
    checkAuth,
  } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        try {
          await loginWithSupabase(email, password);
        } catch {
          await storeLogin(email, password);
        }
        navigate('/dashboard');
      } catch (error) {
        throw error;
      }
    },
    [storeLogin, loginWithSupabase, navigate]
  );

  const logout = useCallback(async () => {
    await storeLogout();
    navigate('/login');
  }, [storeLogout, navigate]);

  const isUiAdministrator = user?.role === 'admin';

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      return user.permissions.includes(permission);
    },
    [user]
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]): boolean => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      return permissions.some((permission) => user.permissions.includes(permission));
    },
    [user]
  );

  const hasAllPermissions = useCallback(
    (permissions: string[]): boolean => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      return permissions.every((permission) => user.permissions.includes(permission));
    },
    [user]
  );

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    clearError,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin: isUiAdministrator,
    isManager: user?.role === 'manager',
    isOperator: user?.role === 'operator',
    isViewer: user?.role === 'viewer',
  };
}

export function usePermission(permission: string): boolean {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
}

export function useRequireAuth(redirectTo: string = '/login'): UseAuthReturn {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [auth.isAuthenticated, auth.isLoading, navigate, redirectTo]);

  return auth;
}

export function useRequirePermission(
  permission: string,
  redirectTo: string = '/dashboard'
): UseAuthReturn {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated && !auth.hasPermission(permission)) {
      navigate(redirectTo, { replace: true });
    }
  }, [auth, permission, navigate, redirectTo]);

  return auth;
}

export default useAuth;
